/**
 * <LauncherGrid /> — the tile-grid landing page at start.pulsebusiness.ai.
 *
 * Wraps content in <PulseShell> so the launcher inherits the cross-product
 * top-bar (logo + account menu). The shell's `currentApp="launcher"` lights
 * up the launcher chip and lets the topbar's Apps button degrade gracefully.
 *
 * Loads apps from the agent-hub Worker via {@link useUserApps}. Renders:
 *   - loading skeleton while in flight
 *   - friendly error when the fetch fails
 *   - empty state when the user has zero app grants
 *   - a tile per app otherwise (with role badge from `app_roles`)
 */

import type { ReactElement } from "react";
import { PulseShell } from "@pulse-business-ai/shell";
import type {
  AuthSessionSnapshot,
  PulseRoleSlug,
} from "@pulse-business-ai/types";

import { useUserApps } from "../hooks/useUserApps.js";
import { AppTile, type LauncherApp } from "./AppTile.js";

export interface LauncherGridProps {
  readonly session: AuthSessionSnapshot;
  readonly onSignOut?: () => void | Promise<void>;
}

function GridContainer({ children }: { children: ReactElement | ReactElement[] }): JSX.Element {
  return (
    <div
      data-testid="launcher-grid"
      style={{
        display: "grid",
        gap: "1rem",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        padding: "2rem",
        maxWidth: "1100px",
        margin: "0 auto",
      }}
    >
      {children}
    </div>
  );
}

export function LauncherGrid({ session, onSignOut }: LauncherGridProps): JSX.Element {
  const { apps, appRoles, loading, error } = useUserApps(session.accessToken);

  return (
    <PulseShell
      topBar={{
        currentApp: "launcher",
        user: session.user,
        roles: session.user?.roles ?? [],
        onSignOut,
      }}
    >
      <section
        aria-labelledby="launcher-heading"
        style={{ padding: "2rem 2rem 0" }}
      >
        <h1
          id="launcher-heading"
          style={{
            margin: 0,
            fontSize: "1.5rem",
            fontWeight: 700,
            color: "var(--pulse-text-strong, #0f172a)",
          }}
        >
          Your Pulse apps
        </h1>
        <p
          style={{
            margin: "0.35rem 0 0",
            color: "var(--pulse-text-muted, #475569)",
          }}
        >
          Pick a destination — your role appears on each tile.
        </p>
      </section>

      {loading ? (
        <div
          role="status"
          aria-live="polite"
          data-testid="launcher-loading"
          style={{ padding: "2rem", color: "var(--pulse-text-muted, #475569)" }}
        >
          Loading your apps…
        </div>
      ) : error ? (
        <div
          role="alert"
          data-testid="launcher-error"
          style={{
            padding: "1rem 2rem",
            color: "var(--pulse-red-700, #b91c1c)",
          }}
        >
          Apps are unavailable right now. Try again in a moment.
        </div>
      ) : apps.length === 0 ? (
        <div
          data-testid="launcher-empty"
          style={{
            padding: "2rem",
            color: "var(--pulse-text-muted, #475569)",
          }}
        >
          No apps yet. Ask an admin to grant access from the Hub Governance
          page.
        </div>
      ) : (
        <GridContainer>
          {apps.map((app: LauncherApp) => (
            <AppTile
              key={app}
              app={app}
              role={appRoles[app] as PulseRoleSlug | undefined}
            />
          ))}
        </GridContainer>
      )}
    </PulseShell>
  );
}
